import { Button, makeStyles, Tooltip, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CollapsableCard from 'components/Generic/CollapsableCard';
import Loading from 'components/Loading';
import { AppContext } from 'context';
import { useClosures, useFirstClosure, useLastClosedOrReopenClosure } from 'hooks';
import { ColorTypeEnum } from 'theme';
import { Closure, ClosureStatusEnum, Event, UserRole } from 'types';
import { useLatestClosure } from 'utils/closureHooks';
import { formatMonthForDisplay } from 'utils/date';
import { putReopenMultiClosure } from 'services/apiOperations';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { notify } from 'components/Notifier';
import ReopenMultiModal from './ReopenMultiModal';

const useStyles = makeStyles(theme => ({
  infoDisplay: {
    width: '165px',
    textTransform: 'capitalize',
  },
  collapsableContent: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    justifyContent: 'flex-end',
  },
  filterContent: {
    display: 'flex',
    clear: 'both',
    margin: 'auto',
    marginBottom: '25px',
    maxWidth: '942px',
  },
  statusToCloseDisplay: {
    color: theme.palette.warning.main,
  },
  statusClosedDisplay: {
    color: theme.palette.primary.main,
  },
  statusReopenDisplay: {
    color: theme.palette.error.main,
  },
}));

const closureCardColorByStatus = {
  [ClosureStatusEnum.TO_CLOSE]: ColorTypeEnum.WARNING,
  [ClosureStatusEnum.CLOSED]: ColorTypeEnum.PRIMARY,
  [ClosureStatusEnum.REOPEN]: ColorTypeEnum.ERROR,
};

const ClosureList: React.FC = () => {
  const context = React.useContext(AppContext);
  const { t } = useTranslation();
  const classes = useStyles();

  const sortingCenter = context?.getSortingCenter?.();
  const minYear = sortingCenter ? new Date(sortingCenter.initializationDate).getFullYear() : null;
  const minYearDate = minYear ? new Date(minYear, 0, 1, 0, 0) : null;

  // Search of first cloture not closed :
  const [nextClosureDate, setNextClosureDate] = React.useState<Date | null>(null);
  const [contextYear, setContextYear] = React.useState<number | undefined>();
  const [isReopenClosureLoading, setIsReopenClosureLoading] = useState(false);
  const [reopenModalVisible, setReopenModalVisible] = useState(false);
  const [reopenModalMonthForDisplay, setReopenModalMonthForDisplay] = useState('');
  const [closureToReopenYear, setClosureToReopenYear] = useState(0);
  const [yearOfNextClosureClosed, setYearOfNextClosureClosed] = useState(0);
  const [reopenModalClosureId, setReopenModalClosureId] = useState(0);
  const [latestClosure, loadingLatest] = useLatestClosure();
  const firstClosure = useFirstClosure(!latestClosure && !loadingLatest);
  const [closures] = useClosures(contextYear);

  const highestYearMonthClosed = useLastClosedOrReopenClosure();

  const isDeClotureAllowed = (role: any, highestYearMonthClosed: Closure | false, year: number): boolean => {
    if (!highestYearMonthClosed || !year) {
      return false;
    }

    const isYearValid = year >= highestYearMonthClosed.year - 1;
    const isRoleSupport = roles.includes(UserRole.ROLE_SUPPORT);

    return isRoleSupport || isYearValid;
  };

  useEffect(() => {
    if (latestClosure && latestClosure?.year && latestClosure?.month) {
      const latestClosureDate = new Date(latestClosure?.year, latestClosure?.month, 0);
      const nextClosureDateTmp = new Date(latestClosureDate.getTime());
      nextClosureDateTmp.setDate(1);
      nextClosureDateTmp.setMonth(nextClosureDateTmp.getMonth() + 1);
      setNextClosureDate(nextClosureDateTmp);
      setContextYear(nextClosureDateTmp?.getFullYear());
    } else if (firstClosure) {
      const nextClosureDateTmp = new Date(firstClosure.year, firstClosure.month - 1);
      setNextClosureDate(nextClosureDateTmp);
      setContextYear(nextClosureDateTmp?.getFullYear());
    }
  }, [latestClosure, firstClosure]);

  const closureCardClassNameByStatus = {
    [ClosureStatusEnum.TO_CLOSE]: classes.statusToCloseDisplay,
    [ClosureStatusEnum.CLOSED]: classes.statusClosedDisplay,
    [ClosureStatusEnum.REOPEN]: classes.statusReopenDisplay,
  };

  const roles = context.getRoles?.();
  const isUserAbleToClose =
    sortingCenter?.isActive && roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());

  const handlerOnChange = (at: Date) => {
    let contextYearBuild = at.getFullYear();
    if (minYear && contextYearBuild < minYear) {
      contextYearBuild = minYear;
    }
    setContextYear(contextYearBuild);
  };
  const isDisabled = (closure: Closure) => {
    return !(
      nextClosureDate &&
      nextClosureDate.getFullYear() === closure.year &&
      nextClosureDate.getMonth() + 1 === closure.month
    );
  };
  const isClosureInit = (closure: Closure) => {
    const minStartDate = sortingCenter?.initializationDate ? new Date(sortingCenter.initializationDate) : null;

    if (minStartDate) {
      return closure && minStartDate.getFullYear() === closure.year && minStartDate.getMonth() + 1 === closure.month;
    }

    return false;
  };
  const handleClickReopenModal = (closure: Closure) => {
    const year = closure.year;
    const monthInLetter = formatMonthForDisplay(closure.month, year);
    const monthInLetterWithFirstLetterUppercase = monthInLetter.charAt(0).toUpperCase() + monthInLetter.slice(1);
    // Création d'une nouvelle date pour obtenir le mois suivant à partir de latestClosure.
    // Attention le mois est indexé à partir de zéro, pas besoin de faire +1 pour obtenir le mois suivant.
    const nextClosureDateAfterLastClosureClosed = new Date(Number(latestClosure?.year), Number(latestClosure?.month));
    setReopenModalVisible(true);
    setReopenModalClosureId(closure.id);
    setReopenModalMonthForDisplay(`${monthInLetterWithFirstLetterUppercase} ${year}`);
    setClosureToReopenYear(year);
    setYearOfNextClosureClosed(nextClosureDateAfterLastClosureClosed.getFullYear());
  };

  const handleClickValidateReopenMulti = () => {
    if (!sortingCenter) return;

    setReopenModalVisible(false);
    setIsReopenClosureLoading(true);

    putReopenMultiClosure(transformObjectId(sortingCenter), reopenModalClosureId.toString()).then(() => {
      setIsReopenClosureLoading(false);
      PubSub.publish(Event.REFRESH_CLOSURES);
      PubSub.publish(Event.REFRESH_LATEST_CLOSURE);
      notify({
        message: t('closures.reopen.successMessage'),
        severity: 'success',
      });
    });
  };

  return (
    <>
      <div className={classes.filterContent}>
        <p style={{ display: 'flex', alignItems: 'center' }}>
          <Typography>{t('closures.close.displayYear')}</Typography>
          &nbsp;
          <DatePicker
            variant="inline"
            disableToolbar
            autoOk
            views={['year']}
            label={''}
            minDate={minYearDate}
            maxDate={new Date()}
            value={contextYear?.toString()}
            initialFocusedDate={contextYear?.toString()}
            strictCompareDates={false}
            onChange={e => e && handlerOnChange(e)}
          />
        </p>
      </div>

      {closures && !isReopenClosureLoading ? (
        closures.map(closure => (
          <CollapsableCard
            maxWidth="942px"
            key={closure['@id']}
            color={closureCardColorByStatus[closure.status]}
            collapsableContent={
              <>
                {[ClosureStatusEnum.TO_CLOSE, ClosureStatusEnum.REOPEN].includes(closure.status) && (
                  <div className={classes.collapsableContent}>
                    <Tooltip title={isDisabled(closure) ? t('closures.close.disabledTooltip') || '' : ''}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={isDisabled(closure)}
                          component={Link}
                          to={`/closures/${closure.id}/close`}
                        >
                          {t('closures.close.button')}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                )}
                {ClosureStatusEnum.CLOSED === closure.status && (
                  <div className={classes.collapsableContent}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        aria-label="reopen"
                        onClick={() => handleClickReopenModal(closure)}
                        disabled={
                          !isDeClotureAllowed(roles, highestYearMonthClosed, closure.year) || isClosureInit(closure)
                        }
                      >
                        {t('closures.reopen.buttonLabel')}
                      </Button>
                    </span>
                  </div>
                )}
              </>
            }
            disableCollapse={!isUserAbleToClose}
          >
            <Typography className={classes.infoDisplay}>
              {formatMonthForDisplay(closure.month, closure.year)}
            </Typography>
            <Typography className={classes.infoDisplay}>{closure.year}</Typography>
            <Typography className={closureCardClassNameByStatus[closure.status]} data-qa-id={`${closure.status}`}>
              {isClosureInit(closure) ? t(`closures.status.init`) : t(`closures.status.${closure.status}`)}
            </Typography>
          </CollapsableCard>
        ))
      ) : (
        <Loading />
      )}
      <ReopenMultiModal
        isOpen={reopenModalVisible}
        monthInMessage={reopenModalMonthForDisplay}
        onClose={() => setReopenModalVisible(false)}
        onValidate={handleClickValidateReopenMulti}
        closureToReopenYear={closureToReopenYear}
        yearOfNextClosureClosed={yearOfNextClosureClosed}
      />
    </>
  );
};
export default ClosureList;
